import * as React from "react"
import { createPortal } from "react-dom"
import { Download, KeyRound, Mail, X } from "lucide-react"

import { useMotionPreference } from "../utils/motion-preference"

const CV_REQUEST_EVENT = "wf:request-cv"

function startDownload(downloadUrl) {
  const link = document.createElement("a")
  link.href = downloadUrl
  link.download = "joshua-welford-resume.pdf"
  document.body.appendChild(link)
  link.click()
  link.remove()
}

const CVRequestModal = () => {
  const [mounted, setMounted] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [isPresent, setIsPresent] = React.useState(false)
  const [isClosing, setIsClosing] = React.useState(false)
  const [step, setStep] = React.useState("email")
  const [email, setEmail] = React.useState("")
  const [code, setCode] = React.useState("")
  const [token, setToken] = React.useState("")
  const [debugCode, setDebugCode] = React.useState("")
  const [message, setMessage] = React.useState("")
  const [error, setError] = React.useState("")
  const [submitting, setSubmitting] = React.useState(false)
  const [dragY, setDragY] = React.useState(0)
  const [isDragging, setIsDragging] = React.useState(false)
  const [hasDraggedTray, setHasDraggedTray] = React.useState(false)
  const openerRef = React.useRef(null)
  const dialogRef = React.useRef(null)
  const emailInputRef = React.useRef(null)
  const codeInputRef = React.useRef(null)
  const dragStartRef = React.useRef(null)
  const latestDragYRef = React.useRef(0)
  const closeTimerRef = React.useRef(null)
  const { reduceMotion } = useMotionPreference()

  const isMobileTray = React.useCallback(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 640px)").matches,
    []
  )

  const resetFlow = React.useCallback(() => {
    setStep("email")
    setEmail("")
    setCode("")
    setToken("")
    setDebugCode("")
    setMessage("")
    setError("")
    setSubmitting(false)
  }, [])

  const resetTrayDrag = React.useCallback(() => {
    latestDragYRef.current = 0
    dragStartRef.current = null
    setDragY(0)
    setIsDragging(false)
  }, [])

  const closeModal = React.useCallback((options = {}) => {
    if (closeTimerRef.current) return

    if (options.preserveDrag) {
      dragStartRef.current = null
      setIsDragging(false)
      setHasDraggedTray(true)
    } else {
      resetTrayDrag()
      setHasDraggedTray(false)
    }

    setIsClosing(true)

    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null
      setIsPresent(false)
      setIsClosing(false)
      setOpen(false)
      window.setTimeout(() => openerRef.current?.focus?.(), 0)
    }, reduceMotion ? 0 : 220)
  }, [reduceMotion, resetTrayDrag])

  const openModal = React.useCallback(() => {
    openerRef.current = document.activeElement
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    resetFlow()
    resetTrayDrag()
    setHasDraggedTray(false)
    setIsClosing(false)
    setIsPresent(true)
    setOpen(true)
  }, [resetFlow, resetTrayDrag])

  React.useEffect(() => {
    setMounted(true)
  }, [])

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined
    window.addEventListener(CV_REQUEST_EVENT, openModal)
    return () => window.removeEventListener(CV_REQUEST_EVENT, openModal)
  }, [openModal])

  React.useEffect(() => {
    if (!open) return undefined
    setIsPresent(true)
    setIsClosing(false)
    resetTrayDrag()
    setHasDraggedTray(false)

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    window.setTimeout(() => {
      if (step === "code") {
        codeInputRef.current?.focus()
      } else {
        emailInputRef.current?.focus()
      }
    }, 0)

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [open, resetTrayDrag, step])

  React.useEffect(() => () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
    }
  }, [])

  const handleTrayPointerDown = React.useCallback(
    event => {
      if (isClosing) return
      if (!isMobileTray()) return
      if (event.target.closest("button, a, input, textarea, select")) return
      event.preventDefault()
      event.stopPropagation()
      event.currentTarget.setPointerCapture(event.pointerId)
      dragStartRef.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        lastY: event.clientY,
        lastTime: window.performance.now(),
        velocityY: 0,
      }
      latestDragYRef.current = 0
      setHasDraggedTray(true)
      setIsDragging(true)
      setDragY(0)
    },
    [isClosing, isMobileTray]
  )

  const handleTrayPointerMove = React.useCallback(event => {
    const dragStart = dragStartRef.current
    if (!dragStart || dragStart.pointerId !== event.pointerId) return
    event.preventDefault()
    const nextDragY = Math.max(0, event.clientY - dragStart.startY)
    const now = window.performance.now()
    const elapsed = Math.max(1, now - dragStart.lastTime)
    dragStart.velocityY = (event.clientY - dragStart.lastY) / elapsed
    dragStart.lastY = event.clientY
    dragStart.lastTime = now
    latestDragYRef.current = nextDragY
    setDragY(nextDragY)
  }, [])

  const handleTrayPointerEnd = React.useCallback(
    event => {
      const dragStart = dragStartRef.current
      if (!dragStart || dragStart.pointerId !== event.pointerId) return
      event.preventDefault()
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }

      const shouldClose =
        latestDragYRef.current > Math.min(180, window.innerHeight * 0.24) ||
        (dragStart.velocityY > 0.85 && latestDragYRef.current > 24)
      if (shouldClose) {
        closeModal({ preserveDrag: true })
      } else {
        resetTrayDrag()
      }
    },
    [closeModal, resetTrayDrag]
  )

  const handleDialogKeyDown = event => {
    if (event.key === "Escape") {
      event.preventDefault()
      closeModal()
      return
    }

    if (event.key !== "Tab") return

    const focusable = Array.from(
      dialogRef.current?.querySelectorAll(
        "button, input, a, [tabindex]:not([tabindex='-1'])"
      ) || []
    ).filter(element => !element.disabled)

    if (!focusable.length) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const requestCode = async event => {
    event.preventDefault()
    setSubmitting(true)
    setError("")
    setMessage("")

    try {
      const response = await fetch("/api/cv/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      })
      const result = await response.json().catch(() => ({}))

      if (!response.ok || !result.ok) {
        throw new Error(result.error || "Could not send a confirmation code.")
      }

      setToken(result.token)
      setDebugCode(result.debugCode || "")
      setStep("code")
      setMessage(`Check ${email.trim()} for the 6-digit confirmation code.`)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setSubmitting(false)
    }
  }

  const verifyCode = async event => {
    event.preventDefault()
    setSubmitting(true)
    setError("")

    try {
      const response = await fetch("/api/cv/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code, token }),
      })
      const result = await response.json().catch(() => ({}))

      if (!response.ok || !result.ok) {
        throw new Error(result.error || "Could not verify that code.")
      }

      setMessage("Verified. Starting download...")
      startDownload(result.downloadUrl)
      window.setTimeout(closeModal, 700)
    } catch (verifyError) {
      setError(verifyError.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (!mounted) return null

  const isVisible = open || isPresent
  const dragProgress = Math.min(dragY / 280, 1)
  const modalStyle =
    hasDraggedTray && !isClosing
      ? {
          "--cv-request-backdrop-alpha": (0.72 * (1 - dragProgress)).toFixed(3),
          "--cv-request-backdrop-blur": `${Math.max(0, 14 * (1 - dragProgress)).toFixed(1)}px`,
        }
      : undefined
  const dialogStyle =
    hasDraggedTray
      ? {
          transform: `translateY(${dragY}px)`,
          animation: "none",
          transition: isDragging || reduceMotion ? "none" : "transform .22s cubic-bezier(.22, 1, .36, 1)",
        }
      : undefined

  const modal = isVisible ? (
    <div
      className={`cv-request-modal${isClosing ? " is-closing" : ""}`}
      style={modalStyle}
      onPointerDown={event => {
        if (event.target === event.currentTarget) closeModal()
      }}
    >
      <div
        ref={dialogRef}
        className={`cv-request-modal__dialog${isClosing ? " is-closing" : ""}`}
        style={dialogStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cv-request-title"
        onPointerDown={event => event.stopPropagation()}
        onKeyDown={handleDialogKeyDown}
      >
        <div className="cv-request-modal__drag-handle" aria-hidden="true" />
        <div
          className="cv-request-modal__header"
          onPointerDown={handleTrayPointerDown}
          onPointerMove={handleTrayPointerMove}
          onPointerUp={handleTrayPointerEnd}
          onPointerCancel={handleTrayPointerEnd}
          onLostPointerCapture={handleTrayPointerEnd}
        >
          <div className="cv-request-modal__eyebrow">
            {step === "code" ? <KeyRound size={15} aria-hidden="true" /> : <Mail size={15} aria-hidden="true" />}
            <span>Request CV</span>
          </div>
          <button
            type="button"
            className="cv-request-modal__close"
            aria-label="Close CV request"
            onClick={closeModal}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <form className="cv-request-modal__form" onSubmit={step === "code" ? verifyCode : requestCode}>
          <div>
            <h2 id="cv-request-title">
              {step === "code" ? "Confirm your email" : "Get Joshua's CV"}
            </h2>
            <p>
              {step === "code"
                ? "Enter the code sent to your inbox and the download will start."
                : "Enter your email address and I'll send a quick confirmation code first."}
            </p>
          </div>

          {step === "code" ? (
            <label className="cv-request-modal__field">
              <span>Confirmation code</span>
              <input
                ref={codeInputRef}
                value={code}
                onChange={event => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                required
              />
            </label>
          ) : (
            <label className="cv-request-modal__field">
              <span>Email address</span>
              <input
                ref={emailInputRef}
                value={email}
                onChange={event => setEmail(event.target.value)}
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                required
              />
            </label>
          )}

          {message ? <p className="cv-request-modal__message">{message}</p> : null}
          {debugCode ? <p className="cv-request-modal__debug">Local test code: {debugCode}</p> : null}
          {error ? <p className="cv-request-modal__error">{error}</p> : null}

          <div className="cv-request-modal__actions">
            {step === "code" ? (
              <button
                type="button"
                className="cv-request-modal__secondary"
                disabled={submitting}
                onClick={() => {
                  setStep("email")
                  setCode("")
                  setError("")
                  setMessage("")
                }}
              >
                Change email
              </button>
            ) : null}
            <button type="submit" className="cv-request-modal__submit" disabled={submitting}>
              <Download size={16} aria-hidden="true" />
              <span>{submitting ? "Working..." : step === "code" ? "Verify & Download" : "Send Code"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  ) : null

  return createPortal(modal, document.body)
}

export default CVRequestModal
